import type { DataScope } from "../workflowData/models";
import { z } from "zod";
import { getHeaders } from "../../api/api";
import { draftSummarySchema, workflowDraftSchema, type ChatRequest, type WorkflowDraft, type DraftSummary, type EditWorkflowNodeRequest, type EditReminderRequest, type SetReminderEnabledRequest } from "./models";
import { readWorkflowBuilderDeleteResponse, readWorkflowBuilderResponse } from "./response";
import { workflowDraftApiPath } from "./draftApiPath";

export { WorkflowBuilderApiError } from "./response";
export type SaveWorkflowRequest = { expected_revision: number; request_id: string; project_id?: string | null; data_scope?: DataScope | null };

const base = (organizationId?: string) => `${String(import.meta.env.VITE_BACKEND_URI ?? "").replace(/\/$/, "")}${workflowDraftApiPath(organizationId)}`;
async function request(url: string, token: string | undefined, signal: AbortSignal, body?: ChatRequest | SaveWorkflowRequest | EditWorkflowNodeRequest | EditReminderRequest | SetReminderEnabledRequest): Promise<unknown> {
    const response = await fetch(url, {
        method: body ? "POST" : "GET",
        cache: "no-store",
        headers: { ...await getHeaders(token), "Content-Type": "application/json" },
        ...(body ? { body: JSON.stringify(body) } : {}),
        signal
    });
    return readWorkflowBuilderResponse(response);
}
export async function listWorkflowDrafts(token: string | undefined, signal: AbortSignal, organizationId?: string): Promise<DraftSummary[]> {
    return z.object({ drafts: z.array(draftSummarySchema) }).parse(await request(base(organizationId), token, signal)).drafts;
}
export async function getWorkflowDraft(id: string, token: string | undefined, signal: AbortSignal, organizationId?: string): Promise<WorkflowDraft> {
    return z.object({ draft: workflowDraftSchema }).parse(await request(`${base(organizationId)}/${encodeURIComponent(id)}`, token, signal)).draft;
}
export async function sendWorkflowMessage(id: string | undefined, body: ChatRequest, token: string | undefined, signal: AbortSignal, organizationId?: string): Promise<WorkflowDraft> {
    const url = id ? `${base(organizationId)}/${encodeURIComponent(id)}/chat` : base(organizationId);
    return z.object({ draft: workflowDraftSchema }).parse(await request(url, token, signal, body)).draft;
}
export async function deleteWorkflowDraft(id: string, expectedRevision: number, token: string | undefined, signal: AbortSignal, organizationId?: string): Promise<void> {
    const query = new URLSearchParams({ expected_revision: String(expectedRevision) });
    const response = await fetch(`${base(organizationId)}/${encodeURIComponent(id)}?${query}`, {
        method: "DELETE",
        headers: await getHeaders(token),
        signal
    });
    await readWorkflowBuilderDeleteResponse(response);
}
export async function saveWorkflowDraft(id: string, body: SaveWorkflowRequest, token: string | undefined, signal: AbortSignal, organizationId?: string): Promise<WorkflowDraft> {
    return z.object({ draft: workflowDraftSchema }).parse(await request(`${base(organizationId)}/${encodeURIComponent(id)}/save`, token, signal, body)).draft;
}
export async function editWorkflowNode(id: string, body: EditWorkflowNodeRequest, token: string | undefined, signal: AbortSignal, organizationId?: string): Promise<WorkflowDraft> {
    return z.object({ draft: workflowDraftSchema }).parse(await request(`${base(organizationId)}/${encodeURIComponent(id)}/edit`, token, signal, body)).draft;
}
export async function editReminder(id: string, body: EditReminderRequest, token: string | undefined, signal: AbortSignal, organizationId?: string): Promise<WorkflowDraft> {
    return z.object({ draft: workflowDraftSchema }).parse(await request(`${base(organizationId)}/${encodeURIComponent(id)}/reminder/edit`, token, signal, body)).draft;
}
export async function setReminderEnabled(id: string, body: SetReminderEnabledRequest, token: string | undefined, signal: AbortSignal, organizationId?: string): Promise<WorkflowDraft> {
    return z.object({ draft: workflowDraftSchema }).parse(await request(`${base(organizationId)}/${encodeURIComponent(id)}/schedule`, token, signal, body)).draft;
}
